<template>
  <v-card id="tax-rend-cap-mob" class="px-4 pt-2 pb-4 my-0 elevation-0">
    <v-card-title class="px-1">
      <h1 class="card-title">
        Rendimientos del capital mobiliario
      </h1>
    </v-card-title>

    <v-card-text class="mt-0 pt-0 pb-3 px-3">
      <v-row>
        <v-col cols="12" md="12" lg="6" xl="4" class="px-4">
          <TaxTitle
            title="Intereses de cuentas y depósitos"
            :boxNumber="rendCapMob.boxes.interest.number"
          />
          <v-simple-table fixed-header class="mt-4">
            <template v-slot:default>
              <tbody>
                <tr>
                  <td>
                    <span>
                      Intereses de cuentas y depósitos
                      <InfoIcon
                        :size="15"
                        :text="`Suma de todos los intereses recibidos, en caso de haberlos, durante el año fiscal. Filios calcula este valor mediante la suma del total de intereses recibidos añadidos desde la pantalla de movimientos.`"
                      />
                    </span>
                  </td>
                  <td class="text-right input-small items-flex items-flex-end">
                    <currency
                      :value="rendCapMob.boxes.interest.data.totalInterest"
                      :currency="user.baseCurrency"
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    <span>
                      Retenciones
                      <InfoIcon
                        :size="15"
                        :text="`Retenciones en destino ya aplicadas en el bróker para los intereses recibidos durante el año fiscal.`"
                      />
                    </span>
                  </td>
                  <td class="text-right input-small items-flex items-flex-end">
                    <currency
                      :value="rendCapMob.boxes.interest.data.destinationRetentionInterest"
                      :currency="user.baseCurrency"
                    />
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-col>
        <v-col cols="12" md="12" lg="6" xl="4" class="px-4">
          <TaxTitle
            title="Dividendos y demás rendimientos por la participación en fondos propios de entidades"
            :boxNumber="rendCapMob.boxes.dividends.number"
          />
          <v-simple-table fixed-header class="mt-4">
            <template v-slot:default>
              <tbody>
                <tr
                  :class="{
                    'row-advise': rendCapMob.boxes.dividends.data.someIncompleteExchangeRate,
                  }"
                >
                  <td class="items-flex">
                    <span>
                      Ingresos íntegros
                      <InfoIcon
                        :size="15"
                        :text="`Suma del total bruto de los dividendos y los drips cobrados durante el año fiscal`"
                      />
                    </span>
                    <template v-if="rendCapMob.boxes.dividends.data.someIncompleteExchangeRate">
                      <div class="ml-3">
                        <WarningIcon :text="$t(`views.tax.rendCapMob.dividends.warning`)" />
                      </div>
                    </template>
                  </td>
                  <td class="text-right">
                    <currency
                      :value="rendCapMob.boxes.dividends.data.ingresos"
                      :currency="user.baseCurrency"
                      :hideDataNoPlanUser="hideDataNoPlanUser"
                    />
                  </td>
                </tr>
                <tr
                  :class="{
                    'row-advise': rendCapMob.boxes.dividends.data.someIncompleteExchangeRate,
                  }"
                >
                  <td class="items-flex">
                    <span>
                      Retenciones
                      <InfoIcon
                        :size="15"
                        :text="`Retenciones en destino ya aplicadas en el bróker durante el año fiscal. Se calcula en función del tipo de bróker, el país del usuario y el país de las posiciones.`"
                      />
                    </span>
                    <template v-if="rendCapMob.boxes.dividends.data.someIncompleteExchangeRate">
                      <div class="ml-3">
                        <WarningIcon :text="$t(`views.tax.rendCapMob.dividends.warning`)" />
                      </div>
                    </template>
                  </td>
                  <td class="text-right">
                    <currency
                      :value="rendCapMob.boxes.dividends.data.retenciones"
                      :currency="user.baseCurrency"
                    />
                  </td>
                </tr>
                <tr
                  :class="{
                    'row-advise': rendCapMob.boxes.dividends.data.someIncompleteExchangeRate,
                  }"
                >
                  <td class="items-flex">
                    <span>
                      Gastos de administración y depósito
                      <InfoIcon
                        :size="15"
                        :text="`Suma de todos los gastos de comisiones, administración y depósitos durante el año fiscal. Filios calcula este valor mediante la suma del total de comisiones no incluídas ya en los G/P de las operaciones. Es decir, la suma de las comisiones de los dividendos y las comisiones añadidas como movimientos de tipo <Comisión> y configuradas para ser incluídas en fiscalidad.`"
                      />
                    </span>
                    <template v-if="rendCapMob.boxes.dividends.data.someIncompleteExchangeRate">
                      <div class="ml-3">
                        <WarningIcon :text="$t(`views.tax.rendCapMob.dividends.warning`)" />
                      </div>
                    </template>
                  </td>
                  <td class="text-right">
                    <currency
                      :value="rendCapMob.boxes.dividends.data.gastosAdministracion"
                      :currency="user.baseCurrency"
                    />
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-col>
        <v-col cols="12" md="12" lg="6" xl="4" class="px-4">
          <TaxTitle
            title="Rendimientos procedentes de rentas que tengan por causa la imposición de capitales"
            :boxNumber="rendCapMob.boxes.imposicionCapitales.number"
          />
          <v-simple-table fixed-header class="mt-4">
            <template v-slot:default>
              <tbody>
                <tr>
                  <td>
                    <span>
                      Rendimientos
                      <InfoIcon
                        :size="15"
                        text="Suma de todas las recompensas de stake recibidas, en caso de haberlas, durante el año fiscal."
                      />
                    </span>
                  </td>
                  <td class="text-right input-small items-flex items-flex-end">
                    <currency
                      :value="rendCapMob.boxes.imposicionCapitales.data"
                      :currency="user.baseCurrency"
                    />
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-col>
        <v-col cols="12" md="12" lg="6" xl="4" class="px-4">
          <TaxTitle title="Cupones sobre bonos" :boxNumber="rendCapMob.boxes.coupons.number" />
          <v-simple-table fixed-header class="mt-4">
            <template v-slot:default>
              <tbody>
                <tr>
                  <td class="items-flex">
                    <span>
                      Ingresos íntegros
                      <InfoIcon
                        :size="15"
                        :text="`Suma del total bruto de cupones a través de bonos cobrados durante el año fiscal`"
                      />
                    </span>
                  </td>
                  <td class="text-right">
                    <currency
                      :value="rendCapMob.boxes.coupons.data.ingresos"
                      :currency="user.baseCurrency"
                    />
                  </td>
                </tr>
                <tr>
                  <td class="items-flex">
                    <span>
                      Retenciones
                      <InfoIcon
                        :size="15"
                        :text="`Retenciones en destino ya aplicadas en el bróker durante el año fiscal. Se calcula en función del tipo de bróker, el país del usuario y el país de las posiciones.`"
                      />
                    </span>
                  </td>
                  <td class="text-right">
                    <currency
                      :value="rendCapMob.boxes.coupons.data.retenciones"
                      :currency="user.baseCurrency"
                    />
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-col>
        <v-col cols="12" md="12" lg="6" xl="4" class="px-4">
          <TaxTitle
            :title="`Otras ganancias que no derivan de transmisiones imputables a ${yearSelected}`"
            :boxNumber="rendCapMob.boxes.otrasGanancias.number"
          />
          <v-simple-table fixed-header class="mt-4">
            <template v-slot:default>
              <tbody>
                <tr>
                  <td>
                    <span>
                      Otras ganancias
                      <InfoIcon
                        :size="15"
                        text="Suma de todos los bonus/cashback recibidos, en caso de haberlos y estar configurados como incluidos en el IRPF, durante el año fiscal. Por defecto, todos los bonus recibidos son incluidos en el IRPF, pero puedes modificarlo editando la operación."
                      />
                    </span>
                  </td>
                  <td class="text-right input-small items-flex items-flex-end">
                    <currency
                      :value="rendCapMob.boxes.otrasGanancias.data.totalBonus"
                      :currency="user.baseCurrency"
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    <span>
                      Retenciones
                      <InfoIcon
                        :size="15"
                        :text="`Retenciones en destino ya aplicadas en el bróker para los bonus recibidos durante el año fiscal.`"
                      />
                    </span>
                  </td>
                  <td class="text-right input-small items-flex items-flex-end">
                    <currency
                      :value="rendCapMob.boxes.otrasGanancias.data.destinationRetentionBonus"
                      :currency="user.baseCurrency"
                    />
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-col>
        <v-col cols="12" md="12" lg="6" xl="4" class="px-4">
          <TaxTitle
            title="Doble imposición internacional. Rentas incluidas en la base del ahorro"
            :boxNumber="rendCapMob.boxes.dobleImposicion.number"
          />
          <v-simple-table fixed-header class="mt-4">
            <template v-slot:default>
              <tbody>
                <tr
                  :class="{
                    'row-advise': rendCapMob.boxes.dobleImposicion.data.someIncompleteExchangeRate,
                  }"
                >
                  <td class="items-flex">
                    <span>
                      Rendimientos netos reducidos del cap. mobiliario
                      <InfoIcon
                        :size="15"
                        :text="`Suma del total bruto de los dividendos cobrados, para aquellos dividendos con retención en origen, durante el año fiscal`"
                      />
                    </span>
                    <template
                      v-if="rendCapMob.boxes.dobleImposicion.data.someIncompleteExchangeRate"
                    >
                      <div class="ml-3">
                        <WarningIcon :text="$t(`views.tax.rendCapMob.dividends.warning`)" />
                      </div>
                    </template>
                  </td>
                  <td class="text-right">
                    <currency
                      :value="rendCapMob.boxes.dobleImposicion.data.rendimientosNetos"
                      :currency="user.baseCurrency"
                    />
                  </td>
                </tr>
                <tr
                  :class="{
                    'row-advise': rendCapMob.boxes.dobleImposicion.data.someIncompleteExchangeRate,
                  }"
                >
                  <td class="items-flex">
                    <span>
                      Impuesto satisfecho en el extranjero
                      <InfoIcon
                        :size="15"
                        :text="`Suma de todas las retenciones que deben ser devueltas para aquellos dividendos con retención en origen, durante el año fiscal`"
                      />
                    </span>
                    <template
                      v-if="rendCapMob.boxes.dobleImposicion.data.someIncompleteExchangeRate"
                    >
                      <div class="ml-3">
                        <WarningIcon :text="$t(`views.tax.rendCapMob.dividends.warning`)" />
                      </div>
                    </template>
                  </td>
                  <td class="text-right">
                    <currency
                      :value="rendCapMob.boxes.dobleImposicion.data.impuestoSatisfechoExtranjero"
                      :currency="user.baseCurrency"
                      :hideDataNoPlanUser="hideDataNoPlanUser"
                    />
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapState } from 'vuex'
import Currency from '@/views/components/common/Currency'
import CurrencyInput from '@/views/components/common/CurrencyInput'
import WarningIcon from '@/views/components/common/WarningIcon'
import InfoIcon from '@/views/components/common/InfoIcon'
import TaxTitle from '@/views/components/tax/taxDocs/renta/TaxTitle'

export default {
  components: { Currency, CurrencyInput, WarningIcon, InfoIcon, TaxTitle },
  props: {
    yearSelected: {
      type: String,
      default: null,
    },
    rendCapMob: {
      type: Object,
      default: () => {},
    },
    hideDataNoPlanUser: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState(['user']),
  },
}
</script>

<style lang="scss">
#tax-rend-cap-mob {
  .items-flex {
    display: flex;
    align-items: center;
    &.items-flex-end {
      justify-content: flex-end;
    }
  }
  .input-small input {
    width: 40px;
  }
}
</style>
